import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ---------- helpers
// ---------- helpers (top of file, before the component)
const preferLang = (obj) =>
  obj?.en || obj?.ar || (typeof obj === "string" ? obj : "") || "";

const getId = (v) =>
  v && typeof v === "object" ? v._id || v.id || "" : v || "";
const makeMap = (arr, getKey, getVal) =>
  arr.reduce((m, x) => ((m[getKey(x)] = getVal(x)), m), {});

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  const storeId = localStorage.getItem("storeId");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(storeId ? { "X-Store-Id": storeId } : {}),
  };
};
const safeMsg = (e) => {
  const raw = e?.response?.data?.message || e?.message || "Failed to load";
  return String(raw).slice(0, 180); // clamp so it can't stretch the UI
};

const STOCK_XFER_URL = `${API_BASE_URL}api/log/stock-transfers`;
const WAREHOUSES_URL = `${API_BASE_URL}api/warehouses?limit=1000`;
const PRODUCTS_URL = `${API_BASE_URL}api/products?limit=1000`;

// put this near your other helpers

const StockTransferLog = () => {
  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [error, setError] = useState("");

  // filters
  //   const [q, setQ] = useState("");
  const [fromWarehouse, setFromWarehouse] = useState("");
  const [toWarehouse, setToWarehouse] = useState("");
  const [productId, setProductId] = useState("");
  const [startDate, setStartDate] = useState(""); // yyyy-mm-dd
  const [endDate, setEndDate] = useState(""); // yyyy-mm-dd
  const [sort, setSort] = useState("createdAt:desc");

  // dropdown data
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);

  // ---- maps & label helpers (place right after the states above)
  const warehousesMap = useMemo(
    () =>
      makeMap(
        warehouses,
        (w) => w._id || w.id || w.name, // key
        (w) =>
          (typeof w.name === "string" && w.name) ||
          preferLang(w.warehouseName) ||
          w.label ||
          "" // value shown
      ),
    [warehouses]
  );

  const productsMap = useMemo(
    () =>
      makeMap(
        products,
        (p) => p._id || p.id,
        (p) =>
          preferLang(p.name) ||
          preferLang(p.Product_Name) ||
          p.name ||
          p.Product_Name ||
          ""
      ),
    [products]
  );

  const warehouseLabel = (w) => {
    if (!w) return "—";
    // backend-normalized object → w.name is already a string
    if (typeof w.name === "string" && w.name) return w.name;
    // when only id comes in or map lookup
    const id = getId(w);
    return warehousesMap[id] || preferLang(w.warehouseName) || id || "—";
  };

  const productLabel = (p) => {
    if (!p) return "—";
    if (typeof p.name === "string" && p.name) return p.name;
    const n = preferLang(p.name) || preferLang(p.Product_Name);
    if (n) return n;
    const id = getId(p);
    return productsMap[id] || id || "—";
  };

  const variantLabel = (v) =>
    v?.value?.en || v?.value || v?.sku || getId(v) || "—";

  // details
  const [detail, setDetail] = useState(null);

  const params = useMemo(
    () => ({
      page,
      limit,
      sort,
      // ...(q ? { q } : {}),
      ...(fromWarehouse ? { fromWarehouse } : {}),
      ...(toWarehouse ? { toWarehouse } : {}),
      ...(productId ? { productId } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    }),
    [
      page,
      limit,
      sort,
      //   q,
      fromWarehouse,
      toWarehouse,
      productId,
      startDate,
      endDate,
    ]
  );

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError("");
      try {
        const [xfer, whs, prods] = await Promise.all([
          axios.get(STOCK_XFER_URL, {
            params,
            headers: getAuthHeaders(),
            withCredentials: true,
          }),
          axios
            .get(WAREHOUSES_URL, {
              headers: getAuthHeaders(),
              withCredentials: true,
            })
            .catch(() => ({ data: { data: [] } })),
          axios
            .get(PRODUCTS_URL, {
              headers: getAuthHeaders(),
              withCredentials: true,
            })
            .catch(() => ({ data: { data: [] } })),
        ]);

        const {
          data,
          total: t = 0,
          page: p = 1,
          limit: l = limit,
        } = xfer.data || {};
        setRows(Array.isArray(data) ? data : []);
        setTotal(t);
        setPage(p);
        setLimit(l);

        setWarehouses(whs.data?.data || whs.data || []);
        setProducts(prods.data?.data || prods.data || []);
      } catch (e) {
        console.error("Fetch stock transfers failed:", e);
        setError(safeMsg(e));
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // export CSV (refactor to not depend on React state)
  const toCSV = (rows, { productLabel, variantLabel, warehouseLabel }) => {
    const headers = [
      "Date",
      "Ref No",
      "Product",
      "Variant",
      "Qty",
      "From Warehouse",
      "To Warehouse",
      "Notes",
      "Created By",
    ];
    const body = rows.map((r) => [
      new Date(r.createdAt).toLocaleString(),
      r.refNumber || "",
      productLabel(r.product),
      variantLabel(r.variant),
      r.quantityTransferred ?? "",
      warehouseLabel(r.fromWarehouse),
      warehouseLabel(r.toWarehouse),
      r.notes || "",
      r.createdBy?.name || r.createdBy || "",
    ]);
    return [headers, ...body]
      .map((row) =>
        row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
  };

  const onExport = () => {
    const csv = toCSV(rows, { productLabel, variantLabel, warehouseLabel });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-transfer-log_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isRTL =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("dir") === "rtl";

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page header (same structure as product-list) */}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>
                {window.translate
                  ? window.translate("Stock Transfer Logs")
                  : "Stock Transfer Log"}
              </h4>
              <h6>
                {window.translate
                  ? window.translate("Track stock movement between warehouses")
                  : "Track stock movement between warehouses"}
              </h6>
            </div>
          </div>

          {/* top icons (match product-list). If you use OverlayTrigger/ImageWithBasePath, keep them; 
           otherwise simple icons/links are fine. */}
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onExport}
              title="Export CSV"
            >
              <i className="ti ti-download me-1" />
              {window.translate ? window.translate("Export CSV") : "Export CSV"}
            </button>

            <Link to="/stock-transfer" className="btn btn-primary">
              <i className="ti ti-circle-plus me-2 ms-2" />
              {window.translate
                ? window.translate("Add Transfer")
                : "Add Transfer"}
            </Link>
          </div>
        </div>

        {/* Card (same skeleton as product-list) */}
        <div className="card table-list-card">
          {/* Card header: right-aligned dropdown filter toolbar */}
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set" />
            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
              {/* From Warehouse */}
              <div className="dropdown me-2">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                  data-bs-toggle="dropdown"
                >
                  {fromWarehouse
                    ? warehouses.find(
                        (w) => (w._id || w.id || w.name) === fromWarehouse
                      )?.name || "From"
                    : "From Warehouse"}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setFromWarehouse("");
                        setPage(1);
                      }}
                    >
                      All
                    </Link>
                  </li>
                  {warehouses.map((w) => {
                    const val = w._id || w.id || w.name;
                    return (
                      <li key={val}>
                        <Link
                          to="#"
                          className="dropdown-item rounded-1"
                          onClick={() => {
                            setFromWarehouse(val);
                            setPage(1);
                          }}
                        >
                          {w.name || w.label || val}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* To Warehouse */}
              <div className="dropdown me-2">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                  data-bs-toggle="dropdown"
                >
                  {toWarehouse
                    ? warehouses.find(
                        (w) => (w._id || w.id || w.name) === toWarehouse
                      )?.name || "To"
                    : "To Warehouse"}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setToWarehouse("");
                        setPage(1);
                      }}
                    >
                      All
                    </Link>
                  </li>
                  {warehouses.map((w) => {
                    const val = w._id || w.id || w.name;
                    return (
                      <li key={val}>
                        <Link
                          to="#"
                          className="dropdown-item rounded-1"
                          onClick={() => {
                            setToWarehouse(val);
                            setPage(1);
                          }}
                        >
                          {w.name || w.label || val}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Product */}
              <div className="dropdown me-2">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                  data-bs-toggle="dropdown"
                >
                  {productId
                    ? products.find((p) => (p._id || p.id) === productId)
                        ?.name ||
                      products.find((p) => (p._id || p.id) === productId)
                        ?.Product_Name ||
                      "Product"
                    : "Product"}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setProductId("");
                        setPage(1);
                      }}
                    >
                      All
                    </Link>
                  </li>
                  {products.map((p) => {
                    const val = p._id || p.id;
                    const label = p.name || p.Product_Name || val;
                    return (
                      <li key={val}>
                        <Link
                          to="#"
                          className="dropdown-item rounded-1"
                          onClick={() => {
                            setProductId(val);
                            setPage(1);
                          }}
                        >
                          {label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Sort preset (Last 7 Days etc.) */}
              <div className="dropdown me-2">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                  data-bs-toggle="dropdown"
                >
                  {window.translate
                    ? window.translate("sortLast7Days")
                    : "Sort By : Last 7 Days"}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        const t = new Date();
                        const s = new Date();
                        s.setDate(t.getDate() - 7);
                        setStartDate(s.toISOString().slice(0, 10));
                        setEndDate(t.toISOString().slice(0, 10));
                        setPage(1);
                      }}
                    >
                      {window.translate
                        ? window.translate("last7Days")
                        : "Last 7 Days"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        const t = new Date();
                        const s = new Date();
                        s.setDate(t.getDate() - 30);
                        setStartDate(s.toISOString().slice(0, 10));
                        setEndDate(t.toISOString().slice(0, 10));
                        setPage(1);
                      }}
                    >
                      {window.translate
                        ? window.translate("lastMonth")
                        : "Last 30 Days"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        const t = new Date();
                        const s = new Date(t.getFullYear(), t.getMonth(), 1);
                        setStartDate(s.toISOString().slice(0, 10));
                        setEndDate(t.toISOString().slice(0, 10));
                        setPage(1);
                      }}
                    >
                      {window.translate("This Month")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setPage(1);
                      }}
                    >
                      All Time
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Order (createdAt/qty) */}
              <div className="dropdown">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                  data-bs-toggle="dropdown"
                >
                  {window.translate ? window.translate("Order") : "Order"}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setSort("createdAt:desc");
                        setPage(1);
                      }}
                    >
                      {window.translate ? window.translate("Newest") : "Newest"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setSort("createdAt:asc");
                        setPage(1);
                      }}
                    >
                      {window.translate ? window.translate("Oldest") : "Oldest"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setSort("quantityTransferred:desc");
                        setPage(1);
                      }}
                    >
                      {window.translate
                        ? window.translate("Quantity ↓")
                        : "Quantity ↓"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        setSort("quantityTransferred:asc");
                        setPage(1);
                      }}
                    >
                      {window.translate
                        ? window.translate("Quantity ↑")
                        : "Quantity ↑"}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Card body -> table (and error like product-list) */}
          <div className="card-body p-3">
            {error && (
              <div className="alert alert-danger d-flex justify-content-between align-items-center">
                <div>{error}</div>
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => setError("")}
                >
                  Dismiss
                </button>
              </div>
            )}

            <div
              className="table-responsive"
              style={{ border: "1px solid #eee" }}
            >
              {loading ? (
                <p className="mb-0">
                  {window.translate ? window.translate("Loading…") : "Loading…"}
                </p>
              ) : rows.length === 0 ? (
                <p className="mb-0">
                  {window.translate
                    ? window.translate("No stock transfers found")
                    : "No stock transfers found."}
                </p>
              ) : (
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 160 }}>
                        {window.translate("Date")}
                      </th>
                      <th>{window.translate("Ref No")}</th>
                      <th>{window.translate("Product")}</th>
                      <th>{window.translate("Variant")}</th>
                      <th className="text-end">
                        {window.translate("Quantity")}
                      </th>
                      <th>{window.translate("From")}</th>
                      <th>{window.translate("To")}</th>
                      <th style={{ minWidth: 180 }}>
                        {window.translate("Notes")}
                      </th>
                      <th style={{ width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r._id}>
                        <td>{new Date(r.createdAt).toLocaleString()}</td>
                        <td>
                          <span className="badge bg-light text-dark">
                            {r.refNumber}
                          </span>
                        </td>
                        <td>{productLabel(r.product)}</td>
                        <td>{variantLabel(r.variant)}</td>
                        <td className="text-end fw-semibold">
                          {r.quantityTransferred}
                        </td>
                        <td>{warehouseLabel(r.fromWarehouse)}</td>
                        <td>{warehouseLabel(r.toWarehouse)}</td>
                        <td
                          className="text-truncate"
                          title={r.notes || ""}
                          style={{ maxWidth: 220 }}
                        >
                          {r.notes || "—"}
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-white"
                            onClick={() => setDetail(r)}
                          >
                            <i className="ti ti-eye me-1" />{" "}
                            {window.translate("View")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination (same look & placement) */}
            {rows.length > 0 && (
              <div className="d-flex align-items-center justify-content-between mt-3">
                <div className="text-muted">
                  {window.translate("Showing")} {(page - 1) * limit + 1}–
                  {Math.min(page * limit, total)} of {total}
                </div>
                <div className="d-flex align-items-center gap-3">
                  <select
                    className="form-select"
                    style={{ width: 110 }}
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n} / page
                      </option>
                    ))}
                  </select>
                  <div
                    className="btn-group"
                    dir={isRTL ? "rtl" : "ltr"}
                    role="group"
                    aria-label="Pagination"
                  >
                    {/* Prev */}
                    <button
                      className="btn btn-white"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      aria-label={isRTL ? "Next page" : "Previous page"}
                    >
                      <i
                        className={`ti ${
                          isRTL ? "ti-chevron-right" : "ti-chevron-left"
                        }`}
                      />
                    </button>

                    {/* Page indicator */}
                    <button className="btn btn-outline-secondary" disabled>
                      {page} / {Math.max(1, Math.ceil(total / limit))}
                    </button>

                    {/* Next */}
                    <button
                      className="btn btn-white"
                      disabled={page >= Math.ceil(total / limit)}
                      onClick={() => setPage((p) => p + 1)}
                      aria-label={isRTL ? "Previous page" : "Next page"}
                    >
                      <i
                        className={`ti ${
                          isRTL ? "ti-chevron-left" : "ti-chevron-right"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Details modal (unchanged from your version) */}
        <div
          className={`modal fade ${detail ? "show d-block" : ""}`}
          tabIndex="-1"
          style={{ background: detail ? "rgba(0,0,0,.3)" : "transparent" }}
          onClick={() => setDetail(null)}
        >
          <div
            className="modal-dialog modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {window.translate("Stock Transfer")} • {detail?.refNumber}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDetail(null)}
                />
              </div>
              <div className="modal-body">
                {detail && (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="text-muted d-block">
                        {window.translate("Date")}
                      </label>
                      <div>{new Date(detail.createdAt).toLocaleString()}</div>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted d-block">
                        {window.translate("Quantity")}
                      </label>
                      <div className="fw-semibold">
                        {detail.quantityTransferred}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted d-block">
                        {window.translate("From Warehouse")}
                      </label>
                      <div>{warehouseLabel(detail.fromWarehouse)}</div>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted d-block">
                        {window.translate("To Warehouse")}
                      </label>
                      <div>{warehouseLabel(detail.toWarehouse)}</div>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted d-block">
                        {window.translate("Product")}
                      </label>
                      <div>{productLabel(detail.product)}</div>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted d-block">
                        {window.translate("Variant")}
                      </label>
                      <div>{variantLabel(detail.variant)}</div>
                    </div>
                    <div className="col-12">
                      <label className="text-muted d-block">
                        {window.translate("Notes")}
                      </label>
                      <div className="border rounded p-2 bg-light">
                        {detail.notes || "—"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setDetail(null)}
                >
                  
                  {window.translate("Cancel")}
                </button>
                <Link className="btn btn-white" to="#">
                  <i className="ti ti-printer me-1" />{" "}
                  {window.translate("Print")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockTransferLog;
